import { XMLParser } from "fast-xml-parser";
import type { NewsItem } from "@orvis/core";
import type { Feed, FetchText } from "./types.js";
import { defaultFetchText } from "./types.js";

/**
 * RSS 2.0 + Atom feed adapter, replacing the legacy `NewsService` that
 * used react-xml-parser and split-at-first-`>` HTML trimming (see
 * legacy/frontend/<country>/app/services/news.service.ts).
 *
 * Handles both RSS (channel/item) and Atom (feed/entry) shapes. Images
 * are pulled from any of: enclosure, media:content, media:thumbnail,
 * itunes:image, or the first <img src=...> in the description.
 */

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: "@_",
  removeNSPrefix: false,
  parseAttributeValue: false,
  trimValues: true,
});

function pickString(x: unknown): string | undefined {
  if (typeof x === "string") return x;
  if (x && typeof x === "object" && "#text" in (x as Record<string, unknown>)) {
    const t = (x as Record<string, unknown>)["#text"];
    if (typeof t === "string") return t;
  }
  return undefined;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
}

function extractImage(item: Record<string, unknown>): string | undefined {
  const enclosure = item["enclosure"] as Record<string, unknown> | undefined;
  if (enclosure && typeof enclosure["@_url"] === "string") return enclosure["@_url"];

  const mediaContent = item["media:content"] as Record<string, unknown> | undefined;
  if (mediaContent && typeof mediaContent["@_url"] === "string") return mediaContent["@_url"];

  const mediaThumb = item["media:thumbnail"] as Record<string, unknown> | undefined;
  if (mediaThumb && typeof mediaThumb["@_url"] === "string") return mediaThumb["@_url"];

  const itunes = item["itunes:image"] as Record<string, unknown> | undefined;
  if (itunes && typeof itunes["@_href"] === "string") return itunes["@_href"];

  const desc = pickString(item["description"]) ?? pickString(item["summary"]) ?? "";
  const match = /<img [^>]*src=["']([^"']+)["']/i.exec(desc);
  return match?.[1];
}

function toItem(raw: Record<string, unknown>, isAtom: boolean): NewsItem | undefined {
  const title = pickString(raw["title"]);
  if (!title) return undefined;

  const url = isAtom
    ? // Atom link is an element with an href attribute (or possibly an array).
      (() => {
        const l = raw["link"];
        if (Array.isArray(l)) {
          const alt = (l as Record<string, unknown>[]).find(
            (x) => x["@_rel"] === undefined || x["@_rel"] === "alternate"
          );
          return typeof alt?.["@_href"] === "string" ? (alt["@_href"] as string) : undefined;
        }
        if (l && typeof l === "object" && typeof (l as Record<string, unknown>)["@_href"] === "string") {
          return (l as Record<string, string>)["@_href"];
        }
        return typeof l === "string" ? l : undefined;
      })()
    : pickString(raw["link"]);

  if (!url) return undefined;

  const publishedAt =
    pickString(raw["pubDate"]) ??
    pickString(raw["published"]) ??
    pickString(raw["updated"]);

  const descriptionRaw =
    pickString(raw["description"]) ??
    pickString(raw["summary"]) ??
    pickString(raw["content"]);

  const guid = pickString(raw["guid"]) ?? pickString(raw["id"]);

  const out: NewsItem = {
    id: guid ?? url,
    title,
    url,
  };
  if (descriptionRaw) out.description = stripHtml(descriptionRaw);
  if (publishedAt) out.publishedAt = publishedAt;
  const image = extractImage(raw);
  if (image) out.image = image;

  const category = pickString(raw["category"]);
  if (category) out.category = category;

  return out;
}

export function parseFeed(xml: string): NewsItem[] {
  const doc = parser.parse(xml) as Record<string, unknown>;

  const rss = doc["rss"] as Record<string, unknown> | undefined;
  if (rss) {
    const channel = rss["channel"] as Record<string, unknown> | undefined;
    const items = channel?.["item"];
    const list = Array.isArray(items) ? (items as Record<string, unknown>[]) : items ? [items as Record<string, unknown>] : [];
    return list.map((i) => toItem(i, false)).filter((i): i is NewsItem => i !== undefined);
  }

  const feed = doc["feed"] as Record<string, unknown> | undefined;
  if (feed) {
    const entries = feed["entry"];
    const list = Array.isArray(entries) ? (entries as Record<string, unknown>[]) : entries ? [entries as Record<string, unknown>] : [];
    return list.map((i) => toItem(i, true)).filter((i): i is NewsItem => i !== undefined);
  }

  return [];
}

/**
 * Build a Feed<NewsItem> bound to a network layer (defaults to `fetch`).
 */
export function rssFeed(sourceName: string, fetcher: FetchText = defaultFetchText): Feed<NewsItem> {
  return {
    name: sourceName,
    async fetch({ url, init }) {
      const xml = await fetcher(url, init);
      return parseFeed(xml).map((n) => ({ ...n, source: n.source ?? sourceName }));
    },
  };
}
